import { forwardRef, useEffect, useImperativeHandle, useRef } from "react"
import CameraHelper from "./CameraHelper"
import * as TWEEN from "@tweenjs/tween.js"
import * as THREE from "three"
import { Box3, Vector3 } from "three"
import { useThree } from "@react-three/fiber"

const CameraControls = forwardRef((props, ref) => {
  const cameraRef = useRef()
  const { camera, scene } = useThree()
  window.camera = camera

  useImperativeHandle(ref, () => ({
    focusOnObject: () => {
      const object = scene.getObjectByName("satellite-model")
      object.position.set(-2, -1, 2)

      object.rotation.set(0, Math.PI - Math.PI / 12, 0)
      object.scale.set(1, 1, 1)

      const startPosition = {
        x: object.position.x,
        y: object.position.y,
        z: object.position.z,
      }
      const finalPosition = { x: -0.5, y: 1.2, z: -0.5 } // Adjust z to move behind the Earth
      const controlPoint = { x: 0.004, y: 2, z: 0.002 } // Adjust this control point to change the arc's curvature

      // Function to calculate Bezier point
      function quadraticBezier(p0, p1, p2, t) {
        const x =
          Math.pow(1 - t, 2) * p0.x +
          1.2 * (1 - t) * t * p1.x +
          Math.pow(t, 2) * p2.x
        const y =
          Math.pow(1 - t, 2) * p0.y +
          2 * (1 - t) * t * p1.y +
          Math.pow(t, 2) * p2.y
        const z =
          Math.pow(1 - t, 2) * p0.z +
          1.2 * (1 - t) * t * p1.z +
          Math.pow(t, 2) * p2.z
        return { x, y, z }
      }

      // Create a tween
      new TWEEN.Tween({ t: 0 })
        .to({ t: 1 }, 2000)
        .easing(TWEEN.Easing.Cubic.InOut)
        .onUpdate((current) => {
          const pos = quadraticBezier(
            startPosition,
            controlPoint,
            finalPosition,
            current.t
          )
          object.position.set(pos.x, pos.y, pos.z)
        })
        .start()

      // Update the tween on every frame
      function animate(time) {
        requestAnimationFrame(animate) // Request the next frame
        TWEEN.update(time) // Update all tweens
      }
      animate()
      cameraRef.current.setLookAt(0, 0.75, 2, 0, 0.75, 0, true).then(() => {
        cameraRef.current.enabled = false

        let box = new Box3()
        box.min.set(-1.8, 0, 0)
        box.max.set(2, 1.4, 0)
      })
    },

    removePreviousObject: () => {
      const object = scene.getObjectByName("satellite-model")
      object.position.set(-0.5, 1.2, -0.5)
      object.rotation.set(0, Math.PI - Math.PI / 12, 0)
      object.scale.set(1, 1, 1)

      const startPosition = {
        x: object.position.x,
        y: object.position.y,
        z: object.position.z,
      }
      const finalPosition = { x: 3, y: -3, z: -7 } // Adjust z to move behind the Earth

      const controlPoint = { x: 2.004, y: 2, z: 0.002 } // Adjust this control point to change the arc's curvature

      // Function to calculate Bezier point
      function quadraticBezier(p0, p1, p2, t) {
        const x =
          Math.pow(1 - t, 2) * p0.x +
          1.2 * (1 - t) * t * p1.x +
          Math.pow(t, 2) * p2.x
        const y =
          Math.pow(1 - t, 2) * p0.y +
          2 * (1 - t) * t * p1.y +
          Math.pow(t, 2) * p2.y
        const z =
          Math.pow(1 - t, 2) * p0.z +
          1.2 * (1 - t) * t * p1.z +
          Math.pow(t, 2) * p2.z
        return { x, y, z }
      }

      // Create a tween for the satellite's position
      new TWEEN.Tween({ t: 0 })
        .to({ t: 1 }, 2000)
        .easing(TWEEN.Easing.Cubic.InOut)
        .onUpdate((current) => {
          const pos = quadraticBezier(
            startPosition,
            controlPoint,
            finalPosition,
            current.t
          )
          object.position.set(pos.x, pos.y, pos.z)
        })
        .start()

      // Update the tween on every frame
      function animate(time) {
        requestAnimationFrame(animate) // Request the next frame
        TWEEN.update(time) // Update all tweens
      }
      animate()
      cameraRef.current.setLookAt(0, 0.75, 2, 0, 0.75, 0, true).then(() => {
        cameraRef.current.enabled = false

        let box = new Box3()
        box.min.set(-1.8, 0, 0)
        box.max.set(2, 1.4, 0)
      })
    },
    zoomToEarch: () => {
      return cameraRef.current.setLookAt(0, 0, 1.5, 0, 0, 0, true)
    },
    zoomToContinent: () => {
      return cameraRef.current.setLookAt(1, 3, 1.5, 0, 0, 0, true)
    },
    resetState: () => {
      const object = scene.getObjectByName("satellite-model")
      object.rotation.set(0, 0, 0)
      object.scale.set(1, 1, 1)
      resetCamera()
    },
  }))

  useEffect(() => {
    cameraRef.current.minPolarAngle = Math.PI / 2
    cameraRef.current.maxPolarAngle = Math.PI / 2
    cameraRef.current.maxZoom = 1
    cameraRef.current.minZoom = 1
    cameraRef.current.dollySpeed = 0

    resetCamera()
  }, [])

  const resetCamera = () => {
    cameraRef.current.enabled = false
    cameraRef.current.setLookAt(0, 0, 3, 0, 0, 0, false).then(() => {
      let box = new Box3()
      box.min.set(-2.2, -1.4, 0)
      box.max.set(2.2, 1.4, 0)
      cameraRef.current.fitToBox(box, true).then(() => {
        cameraRef.current.saveState()
      })
    })
  }

  return (
    <>
      <CameraHelper ref={cameraRef} smoothTime={1} />
    </>
  )
})
export default CameraControls
